import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Plus, ShieldCheck, Trash2, Loader2, Users as UsersIcon, X, RotateCcw, AlertTriangle } from 'lucide-react';
import { PageHeader } from '@/components/shared/PageHeader';
import { EmptyState } from '@/components/shared/EmptyState';
import { TableSkeleton } from '@/components/shared/TableSkeleton';
import { adminApi } from '@/services/api';
import { formatDate, getInitials } from '@/utils/format';
import { Avatar, AvatarFallback, Badge, Button, Card, CardContent, Input, useConfirm } from '@nepal-ecom/ui';

interface Staff {
  id: string;
  email: string | null;
  phone: string | null;
  firstName: string | null;
  lastName: string | null;
  permissions: string[];
  isActive: boolean;
  createdAt: string;
}

interface Permission {
  slug: string;
  label: string;
  group: string;
  description?: string;
}

export function StaffList() {
  const qc = useQueryClient();
  const confirm = useConfirm();
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<Staff | null>(null);

  const { data: staffList, isLoading } = useQuery({
    queryKey: ['admin', 'staff'],
    queryFn: async () => {
      const res = await adminApi.getStaffList();
      return res.data.data as Staff[];
    },
  });

  const { data: catalog } = useQuery({
    queryKey: ['admin', 'staff', 'catalog'],
    queryFn: async () => {
      const res = await adminApi.getStaffPermissions();
      return res.data.data as Permission[];
    },
  });

  const deactivateMutation = useMutation({
    mutationFn: (id: string) => adminApi.deactivateStaff(id),
    onSuccess: () => {
      toast.success('Staff deactivated');
      qc.invalidateQueries({ queryKey: ['admin', 'staff'] });
    },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Failed'),
  });

  const reactivateMutation = useMutation({
    mutationFn: (id: string) => adminApi.reactivateStaff(id),
    onSuccess: () => {
      toast.success('Staff reactivated');
      qc.invalidateQueries({ queryKey: ['admin', 'staff'] });
    },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Failed to reactivate'),
  });

  const hardDeleteMutation = useMutation({
    mutationFn: (id: string) => adminApi.hardDeleteStaff(id),
    onSuccess: () => {
      toast.success('Staff deleted permanently');
      qc.invalidateQueries({ queryKey: ['admin', 'staff'] });
    },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Failed to delete'),
  });

  return (
    <div className="animate-fade-in">
      <PageHeader
        title="Staff"
        description={`${staffList?.length ?? 0} team members with admin access`}
        actions={
          <Button size="sm" onClick={() => setCreating(true)}>
            <Plus className="h-4 w-4" /> Add staff member
          </Button>
        }
      />

      {isLoading ? (
        <TableSkeleton rows={4} cols={5} />
      ) : staffList && staffList.length === 0 ? (
        <Card>
          <CardContent>
            <EmptyState
              icon={UsersIcon}
              title="No staff yet"
              description="Add team members with scoped permissions so they don't need full super_admin access."
            />
          </CardContent>
        </Card>
      ) : (
        <Card className="overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50">
                {['Staff', 'Contact', 'Permissions', 'Status', 'Added', ''].map((h) => (
                  <th key={h} className="px-4 py-3 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {staffList?.map((s) => {
                const name = [s.firstName, s.lastName].filter(Boolean).join(' ') || s.email || s.phone || '(unnamed)';
                return (
                  <tr key={s.id} className="hover:bg-slate-50">
                    <td className="px-4 py-3.5">
                      <div className="flex items-center gap-3">
                        <Avatar className="h-8 w-8"><AvatarFallback>{getInitials(name)}</AvatarFallback></Avatar>
                        <p className="text-sm font-medium text-slate-900">{name}</p>
                      </div>
                    </td>
                    <td className="px-4 py-3.5 text-xs">
                      {s.email && <p className="text-slate-700">{s.email}</p>}
                      {s.phone && <p className="text-slate-500">{s.phone}</p>}
                    </td>
                    <td className="px-4 py-3.5">
                      <div className="flex flex-wrap gap-1 max-w-md">
                        {s.permissions.length === 0 ? (
                          <span className="text-xs text-slate-400 italic">No permissions</span>
                        ) : s.permissions.slice(0, 4).map((p) => (
                          <span key={p} className="px-1.5 py-0.5 rounded bg-slate-100 text-slate-700 text-[10px] font-mono">
                            {p}
                          </span>
                        ))}
                        {s.permissions.length > 4 && (
                          <span className="text-xs text-slate-500">+{s.permissions.length - 4} more</span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3.5">
                      <Badge variant={s.isActive ? 'success' : 'secondary'}>
                        {s.isActive ? 'Active' : 'Deactivated'}
                      </Badge>
                    </td>
                    <td className="px-4 py-3.5 text-xs text-slate-500">{formatDate(s.createdAt)}</td>
                    <td className="px-4 py-3.5">
                      <div className="flex gap-1">
                        <Button
                          size="sm"
                          variant="ghost"
                          title="Edit permissions"
                          onClick={() => setEditing(s)}
                        >
                          <ShieldCheck className="h-4 w-4" />
                        </Button>
                        {s.isActive ? (
                          <Button
                            size="sm"
                            variant="ghost"
                            title="Deactivate"
                            onClick={async () => {
                              if (await confirm({
                                title: `Deactivate ${name}?`,
                                description: "They'll lose admin access but their account stays in the system. You can reactivate later.",
                                confirmLabel: 'Deactivate',
                                tone: 'danger',
                              })) {
                                deactivateMutation.mutate(s.id);
                              }
                            }}
                            className="text-orange-600 hover:bg-orange-50"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        ) : (
                          <>
                            <Button
                              size="sm"
                              variant="ghost"
                              title="Reactivate"
                              onClick={() => reactivateMutation.mutate(s.id)}
                              className="text-emerald-600 hover:bg-emerald-50"
                            >
                              <RotateCcw className="h-4 w-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              title="Delete forever"
                              onClick={async () => {
                                // High-friction gate: the admin must type DELETE
                                // so muscle memory (Enter on a plain confirm)
                                // can't wipe a row. The dialog keeps the confirm
                                // button disabled until the token matches.
                                if (!(await confirm({
                                  title: `Permanently delete ${name}?`,
                                  description: 'This removes the account from the database — no undo.',
                                  confirmLabel: 'Delete forever',
                                  tone: 'danger',
                                  requireText: 'DELETE',
                                }))) return;
                                hardDeleteMutation.mutate(s.id);
                              }}
                              className="text-red-600 hover:bg-red-50"
                            >
                              <AlertTriangle className="h-4 w-4" />
                            </Button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </Card>
      )}

      {(creating || editing) && catalog && (
        <StaffFormModal
          staff={editing}
          catalog={catalog}
          onClose={() => {
            setCreating(false);
            setEditing(null);
          }}
        />
      )}
    </div>
  );
}

interface RolePreset {
  slug: string;
  label: string;
  description: string;
  permissions: string[];
}

function StaffFormModal({
  staff,
  catalog,
  onClose,
}: {
  staff: Staff | null;
  catalog: Permission[];
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const confirm = useConfirm();
  const isEdit = !!staff;
  const [email, setEmail] = useState(staff?.email ?? '');
  const [phone, setPhone] = useState(staff?.phone ?? '');
  const [firstName, setFirstName] = useState(staff?.firstName ?? '');
  const [lastName, setLastName] = useState(staff?.lastName ?? '');
  const [password, setPassword] = useState('');
  const [permissions, setPermissions] = useState<Set<string>>(
    new Set(staff?.permissions ?? []),
  );
  const [selectedPreset, setSelectedPreset] = useState<string>('');

  // Fetch predefined role presets once per dialog open
  const { data: presets } = useQuery({
    queryKey: ['admin', 'staff', 'role-presets'],
    queryFn: async () => {
      const res = await adminApi.getStaffRolePresets();
      return res.data.data as RolePreset[];
    },
  });

  const applyPreset = async (preset: RolePreset) => {
    // Re-clicking the same preset (or clicking a different one after the admin
    // has manually added extra permissions) used to silently wipe those
    // additions. Confirm before overwriting non-preset perms so the admin
    // doesn't lose work on a click that looks idempotent.
    const presetSet = new Set(preset.permissions);
    const extras = Array.from(permissions).filter((p) => !presetSet.has(p));
    if (extras.length > 0 && selectedPreset !== preset.slug) {
      const ok = await confirm({
        title: `Apply "${preset.label}" preset?`,
        description: `This removes ${extras.length} permission${extras.length === 1 ? '' : 's'} you added on top: ${extras.join(', ')}.`,
        confirmLabel: 'Apply preset',
        tone: 'danger',
      });
      if (!ok) return;
    } else if (extras.length > 0 && selectedPreset === preset.slug) {
      // Re-click of the active preset — admin probably wants to RESET to it,
      // but be explicit so they don't lose extras accidentally.
      const ok = await confirm({
        title: `Reset to "${preset.label}" defaults?`,
        description: `You'll lose ${extras.length} additional permission${extras.length === 1 ? '' : 's'}.`,
        confirmLabel: 'Reset',
        tone: 'danger',
      });
      if (!ok) return;
    }
    setSelectedPreset(preset.slug);
    setPermissions(new Set(preset.permissions));
  };

  const grouped = catalog.reduce<Record<string, Permission[]>>((acc, p) => {
    (acc[p.group] ??= []).push(p);
    return acc;
  }, {});

  const createMutation = useMutation({
    mutationFn: () =>
      adminApi.createStaff({
        email: email || undefined,
        phone: phone || undefined,
        firstName: firstName || undefined,
        lastName: lastName || undefined,
        password,
        permissions: Array.from(permissions),
      }),
    onSuccess: () => {
      toast.success('Staff member added');
      qc.invalidateQueries({ queryKey: ['admin', 'staff'] });
      onClose();
    },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Failed to create staff'),
  });

  const updateMutation = useMutation({
    mutationFn: () => adminApi.updateStaffPermissions(staff!.id, Array.from(permissions)),
    onSuccess: () => {
      toast.success('Permissions updated');
      qc.invalidateQueries({ queryKey: ['admin', 'staff'] });
      onClose();
    },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Failed to update'),
  });

  const toggle = (slug: string) => {
    setSelectedPreset(''); // diverged from any preset
    setPermissions((prev) => {
      const next = new Set(prev);
      if (next.has(slug)) next.delete(slug);
      else next.add(slug);
      return next;
    });
  };

  const toggleGroup = (group: Permission[]) => {
    setSelectedPreset('');
    setPermissions((prev) => {
      const next = new Set(prev);
      const allOn = group.every((p) => next.has(p.slug));
      group.forEach((p) => (allOn ? next.delete(p.slug) : next.add(p.slug)));
      return next;
    });
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-in" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
          <h2 className="text-lg font-bold text-slate-900">
            {isEdit ? `Edit ${staff?.firstName ?? 'staff'} permissions` : 'Add staff member'}
          </h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="px-6 py-5 overflow-y-auto flex-1 space-y-5">
          {!isEdit && (
            <div className="grid grid-cols-2 gap-3">
              <Input placeholder="First name" value={firstName} onChange={(e) => setFirstName(e.target.value)} />
              <Input placeholder="Last name" value={lastName} onChange={(e) => setLastName(e.target.value)} />
              <Input placeholder="Email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
              <Input placeholder="Phone (optional)" value={phone} onChange={(e) => setPhone(e.target.value)} />
              <Input
                placeholder="Initial password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="col-span-2"
              />
            </div>
          )}

          {presets && presets.length > 0 && (
            <div>
              <p className="text-xs uppercase tracking-wide font-semibold text-slate-500 mb-2">
                Quick start — pick a role
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {presets.map((p) => {
                  const isActive = selectedPreset === p.slug;
                  return (
                    <button
                      key={p.slug}
                      type="button"
                      onClick={() => applyPreset(p)}
                      className={`text-left p-3 rounded-lg border transition-colors ${
                        isActive
                          ? 'border-primary-500 bg-primary-50 ring-1 ring-primary-200'
                          : 'border-slate-200 hover:border-slate-300 bg-white'
                      }`}
                    >
                      <p className={`text-sm font-semibold ${isActive ? 'text-primary-800' : 'text-slate-900'}`}>
                        {p.label}
                      </p>
                      <p className="text-[11px] text-slate-500 mt-0.5 leading-snug">{p.description}</p>
                      <p className="text-[10px] text-slate-400 mt-1.5">
                        {p.permissions.length} permission{p.permissions.length !== 1 ? 's' : ''}
                      </p>
                    </button>
                  );
                })}
              </div>
              <p className="text-[11px] text-slate-400 mt-2">
                Picking a role pre-selects its permissions. You can still toggle individual ones below.
              </p>
            </div>
          )}

          <div>
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs uppercase tracking-wide font-semibold text-slate-500">Permissions</p>
              <p className="text-xs text-slate-500 tabular-nums">{permissions.size} of {catalog.length} selected</p>
            </div>
            <div className="space-y-4">
              {Object.entries(grouped).map(([group, items]) => {
                const allOn = items.every((p) => permissions.has(p.slug));
                const someOn = items.some((p) => permissions.has(p.slug));
                return (
                  <div key={group} className="border border-slate-200 rounded-lg p-3">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-sm font-semibold text-slate-800 capitalize">{group}</p>
                      <button
                        type="button"
                        onClick={() => toggleGroup(items)}
                        className="text-xs text-primary-600 hover:underline"
                      >
                        {allOn ? 'Clear all' : someOn ? 'Select all' : 'Select all'}
                      </button>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                      {items.map((p) => (
                        <label key={p.slug} className="flex items-start gap-2 text-xs cursor-pointer p-1.5 rounded hover:bg-slate-50">
                          <input
                            type="checkbox"
                            checked={permissions.has(p.slug)}
                            onChange={() => toggle(p.slug)}
                            className="mt-0.5"
                          />
                          <div>
                            <p className="text-slate-700 font-medium">{p.label}</p>
                            <p className="text-slate-400 font-mono text-[10px]">{p.slug}</p>
                          </div>
                        </label>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        <div className="px-6 py-4 border-t border-slate-200 flex items-center justify-end gap-2 bg-slate-50">
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button
            disabled={
              (isEdit ? updateMutation.isPending : createMutation.isPending) ||
              (!isEdit && (!email || !password))
            }
            onClick={() => (isEdit ? updateMutation.mutate() : createMutation.mutate())}
          >
            {(isEdit ? updateMutation.isPending : createMutation.isPending) && (
              <Loader2 className="h-3 w-3 animate-spin mr-1" />
            )}
            {isEdit ? 'Save permissions' : 'Create staff member'}
          </Button>
        </div>
      </div>
    </div>
  );
}
